import { useState, useEffect } from 'react';
import { patientHistoryAPI, patientsAPI } from '../lib/api';
import { generatePatientHistorySummaryPDF } from '../lib/pdfGenerator';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Loader2,
  Plus,
  Trash2,
  Edit,
  Heart,
  Users,
  Scissors,
  User,
  Pill,
  AlertTriangle,
  CheckCircle,
  Clock,
  Save,
  Cigarette,
  Wine,
  Briefcase,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

// Common medical conditions
const COMMON_CONDITIONS = [
  "Hypertension", "Diabetes Mellitus", "Asthma", "Heart Disease", "Stroke",
  "Kidney Disease", "Liver Disease", "Cancer", "Seizure Disorder", 
  "Sickle Cell Disease", "Thyroid Disease", "Psychiatric Illness",
  "HIV", "Tuberculosis", "COPD", "Arthritis", "Osteoporosis"
];

const RELATIVES = [
  "Mother", "Father", "Sister", "Brother", "Maternal Grandmother", 
  "Maternal Grandfather", "Paternal Grandmother", "Paternal Grandfather",
  "Aunt", "Uncle", "Child"
];

const MEDICATION_ROUTES = [
  "Oral", "IV", "IM", "SC", "Topical", "Inhaled", "Sublingual", "Rectal", "Transdermal"
];

const FREQUENCIES = [
  "Once daily", "Twice daily", "Three times daily", "Four times daily",
  "Every 4 hours", "Every 6 hours", "Every 8 hours", "Every 12 hours",
  "As needed (PRN)", "Weekly", "Monthly"
];

export default function PatientHistoryTab({ patientId, patientName }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('pmh');
  
  // Dialog states
  const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
  const [allergyDialogOpen, setAllergyDialogOpen] = useState(false);
  const [familyDialogOpen, setFamilyDialogOpen] = useState(false);
  const [surgeryDialogOpen, setSurgeryDialogOpen] = useState(false);
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  
  // Form states
  const [newCondition, setNewCondition] = useState({ condition: '', year_diagnosed: '', status: 'active', notes: '' });
  const [newAllergy, setNewAllergy] = useState({ allergen: '', allergy_type: 'drug', severity: 'moderate', reaction: '' });
  const [newFamily, setNewFamily] = useState({ relative: '', condition: '', age_diagnosed: '', is_alive: true, notes: '' });
  const [newSurgery, setNewSurgery] = useState({ procedure: '', body_site: '', year: '', hospital: '', had_complications: false, complications: '' });
  const [newMedication, setNewMedication] = useState({ 
    medication_name: '', dose: '', frequency: '', route: 'Oral', 
    indication: '', start_date: '', is_current: true, is_otc: false, is_supplement: false 
  });
  
  // Social history state
  const [socialHistory, setSocialHistory] = useState({
    smoking_status: 'never',
    packs_per_day: null,
    years_smoked: null,
    alcohol_status: 'none',
    drinks_per_week: null,
    drug_use: 'none',
    drug_details: '',
    occupation: '',
    living_situation: '',
    marital_status: '',
    exercise_frequency: '',
    diet_notes: ''
  });

  useEffect(() => {
    loadHistory();
  }, [patientId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const [historyRes, patientRes] = await Promise.all([
        patientHistoryAPI.get(patientId),
        patientsAPI.getById(patientId),
      ]);
      setHistory(historyRes.data);
      setPatient(patientRes.data);
      if (historyRes.data.social_history) {
        setSocialHistory(historyRes.data.social_history);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setLoading(false);
  };

  const handleAddCondition = async () => {
    if (!newCondition.condition) {
      toast.error('Please enter a condition');
      return;
    }
    setSaving(true);
    try {
      await patientHistoryAPI.addCondition(patientId, newCondition);
      toast.success('Condition added');
      setConditionDialogOpen(false);
      setNewCondition({ condition: '', year_diagnosed: '', status: 'active', notes: '' });
      loadHistory();
    } catch (err) {
      toast.error('Failed to add condition');
    }
    setSaving(false);
  };

  const handleAddAllergy = async () => {
    if (!newAllergy.allergen) {
      toast.error('Please enter an allergen');
      return;
    }
    setSaving(true);
    try {
      await patientHistoryAPI.addAllergy(patientId, newAllergy);
      toast.success('Allergy added');
      setAllergyDialogOpen(false);
      setNewAllergy({ allergen: '', allergy_type: 'drug', severity: 'moderate', reaction: '' });
      loadHistory();
    } catch (err) {
      toast.error('Failed to add allergy');
    }
    setSaving(false);
  };

  const handleAddFamilyHistory = async () => {
    if (!newFamily.relative || !newFamily.condition) {
      toast.error('Please enter relative and condition');
      return;
    }
    setSaving(true);
    try {
      const updatedFamilyHistory = [...(history?.family_history || []), newFamily];
      await patientHistoryAPI.update(patientId, { family_history: updatedFamilyHistory });
      toast.success('Family history added');
      setFamilyDialogOpen(false);
      setNewFamily({ relative: '', condition: '', age_diagnosed: '', is_alive: true, notes: '' });
      loadHistory();
    } catch (err) {
      toast.error('Failed to add family history');
    }
    setSaving(false);
  };

  const handleAddSurgery = async () => {
    if (!newSurgery.procedure) {
      toast.error('Please enter a procedure');
      return;
    }
    setSaving(true);
    try {
      const updatedSurgicalHistory = [...(history?.surgical_history || []), newSurgery];
      await patientHistoryAPI.update(patientId, { surgical_history: updatedSurgicalHistory });
      toast.success('Surgery added');
      setSurgeryDialogOpen(false);
      setNewSurgery({ procedure: '', body_site: '', year: '', hospital: '', had_complications: false, complications: '' });
      loadHistory();
    } catch (err) {
      toast.error('Failed to add surgery');
    }
    setSaving(false);
  };

  const handleAddMedication = async () => {
    if (!newMedication.medication_name) {
      toast.error('Please enter medication name');
      return;
    }
    setSaving(true);
    try {
      await patientHistoryAPI.addMedication(patientId, newMedication);
      toast.success('Medication added');
      setMedicationDialogOpen(false);
      setNewMedication({ 
        medication_name: '', dose: '', frequency: '', route: 'Oral', 
        indication: '', start_date: '', is_current: true, is_otc: false, is_supplement: false 
      });
      loadHistory();
    } catch (err) {
      toast.error('Failed to add medication');
    }
    setSaving(false);
  };

  const handleSaveSocialHistory = async () => {
    setSaving(true);
    try {
      await patientHistoryAPI.update(patientId, { social_history: socialHistory });
      toast.success('Social history saved');
      loadHistory();
    } catch (err) {
      toast.error('Failed to save social history');
    }
    setSaving(false);
  };

  const handleGenerateHistoryPDF = async () => {
    if (!patient) {
      toast.error('Patient data not loaded');
      return;
    }
    setGeneratingPDF(true);
    try {
      await generatePatientHistorySummaryPDF({
        patient,
        history,
        generatedBy: user?.full_name || 'Physician',
        generatedByPhone: user?.personal_phone,
      });
      toast.success('History summary PDF downloaded!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF');
    }
    setGeneratingPDF(false);
  };


  const handleDeleteItem = async (section, index) => {
    setSaving(true);
    try {
      const updatedSection = [...(history?.[section] || [])];
      updatedSection.splice(index, 1);
      await patientHistoryAPI.update(patientId, { [section]: updatedSection });
      toast.success('Item removed');
      loadHistory();
    } catch (err) {
      toast.error('Failed to remove item');
    }
    setSaving(false);
  };

  // Calculate pack-years
  const packYears = socialHistory.packs_per_day && socialHistory.years_smoked
    ? (socialHistory.packs_per_day * socialHistory.years_smoked).toFixed(1)
    : null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800 border-red-200';
      case 'managed': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'mild': return 'bg-yellow-100 text-yellow-800';
      case 'moderate': return 'bg-orange-100 text-orange-800';
      case 'severe': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-history-tab">
      {/* Top action bar */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Patient History</h3>
        <Button 
          onClick={handleGenerateHistoryPDF} 
          disabled={generatingPDF}
          className="gap-2 rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
          data-testid="print-history-pdf-btn"
        >
          {generatingPDF ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4" />
          )}
          {generatingPDF ? 'Generating...' : 'Download History Summary'}
        </Button>
      </div>

      {/* Allergy Alert Banner */}
      {history?.allergies?.length > 0 && history.allergies.some(a => a.severity === 'severe') && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="allergy-alert-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Critical Allergy Alert</p>
            <p className="text-xs text-red-600">
              {history.allergies.filter(a => a.severity === 'severe').map(a => a.allergen).join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* History Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('pmh')} data-testid="summary-card-conditions">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <Heart className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="conditions-count">{history?.past_medical_history?.length || 0}</p>
              <p className="text-xs text-slate-500">Conditions</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('allergies')} data-testid="summary-card-allergies">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="allergies-count">{history?.allergies?.length || 0}</p>
              <p className="text-xs text-slate-500">Allergies</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('family')} data-testid="summary-card-family">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="family-count">{history?.family_history?.length || 0}</p>
              <p className="text-xs text-slate-500">Family Hx</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('surgical')} data-testid="summary-card-surgical">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <Scissors className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="surgical-count">{history?.surgical_history?.length || 0}</p>
              <p className="text-xs text-slate-500">Surgeries</p>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('medications')} data-testid="summary-card-medications">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Pill className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="medications-count">{history?.medications?.filter(m => m.is_current)?.length || 0}</p>
              <p className="text-xs text-slate-500">Medications</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="pmh">Medical Hx</TabsTrigger>
          <TabsTrigger value="allergies">Allergies</TabsTrigger>
          <TabsTrigger value="family">Family Hx</TabsTrigger>
          <TabsTrigger value="surgical">Surgical Hx</TabsTrigger>
          <TabsTrigger value="social">Social Hx</TabsTrigger>
          <TabsTrigger value="medications">Medications</TabsTrigger>
        </TabsList>

        {/* Past Medical History */}
        <TabsContent value="pmh">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  Past Medical History
                </CardTitle>
                <CardDescription>Chronic conditions and diagnoses</CardDescription>
              </div>
              <Button onClick={() => setConditionDialogOpen(true)} size="sm" data-testid="add-condition-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Condition
              </Button>
            </CardHeader>
            <CardContent>
              {history?.past_medical_history?.length > 0 ? (
                <div className="space-y-3">
                  {history.past_medical_history.map((condition, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(condition.status)} variant="outline">
                          {condition.status}
                        </Badge>
                        <div>
                          <p className="font-medium">{condition.condition}</p>
                          <p className="text-sm text-slate-500">
                            {condition.year_diagnosed && `Diagnosed: ${condition.year_diagnosed}`}
                            {condition.notes && ` • ${condition.notes}`}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteItem('past_medical_history', idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No medical history recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allergies */}
        <TabsContent value="allergies">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Allergies
                </CardTitle>
                <CardDescription>Drug, food, and environmental allergies</CardDescription>
              </div>
              <Button onClick={() => setAllergyDialogOpen(true)} size="sm" data-testid="add-allergy-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Allergy
              </Button>
            </CardHeader>
            <CardContent>
              {history?.allergies?.length > 0 ? (
                <div className="space-y-3">
                  {history.allergies.map((allergy, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <div className="flex items-center gap-3">
                        <Badge className={getSeverityColor(allergy.severity)}>
                          {allergy.severity}
                        </Badge>
                        <div>
                          <p className="font-medium">{allergy.allergen}</p>
                          <p className="text-sm text-slate-500">
                            {allergy.allergy_type} allergy
                            {allergy.reaction && ` • Reaction: ${allergy.reaction}`}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteItem('allergies', idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No allergies recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Family History */}
        <TabsContent value="family">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Family History
                </CardTitle>
                <CardDescription>Medical conditions in family members</CardDescription>
              </div>
              <Button onClick={() => setFamilyDialogOpen(true)} size="sm" data-testid="add-family-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Entry
              </Button>
            </CardHeader>
            <CardContent>
              {history?.family_history?.length > 0 ? (
                <div className="space-y-3">
                  {history.family_history.map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div>
                        <p className="font-medium">{entry.relative}</p>
                        <p className="text-sm">{entry.condition}</p>
                        <p className="text-xs text-slate-500">
                          {entry.age_diagnosed && `Diagnosed at age ${entry.age_diagnosed}`}
                          {entry.is_alive !== null && ` • ${entry.is_alive ? 'Living' : 'Deceased'}`}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteItem('family_history', idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No family history recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Surgical History */}
        <TabsContent value="surgical">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Scissors className="h-5 w-5 text-purple-500" />
                  Surgical History
                </CardTitle>
                <CardDescription>Past surgeries and procedures</CardDescription>
              </div>
              <Button onClick={() => setSurgeryDialogOpen(true)} size="sm" data-testid="add-surgery-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Surgery
              </Button>
            </CardHeader>
            <CardContent>
              {history?.surgical_history?.length > 0 ? (
                <div className="space-y-3">
                  {history.surgical_history.map((surgery, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div>
                        <p className="font-medium">{surgery.procedure}</p>
                        <p className="text-sm text-slate-600">
                          {surgery.body_site && `${surgery.body_site} • `}
                          {surgery.year && `${surgery.year}`}
                          {surgery.hospital && ` • ${surgery.hospital}`}
                        </p>
                        {surgery.had_complications && (
                          <p className="text-xs text-red-600 mt-1">
                            ⚠ Complications: {surgery.complications}
                          </p>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteItem('surgical_history', idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No surgical history recorded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social History */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-slate-500" />
                Social History
              </CardTitle>
              <CardDescription>Lifestyle factors and social information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Smoking */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Cigarette className="h-4 w-4 text-slate-500" />
                    <Label className="font-semibold">Smoking</Label>
                  </div>
                  <Select 
                    value={socialHistory.smoking_status} 
                    onValueChange={(v) => setSocialHistory(prev => ({ ...prev, smoking_status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="former">Former Smoker</SelectItem>
                      <SelectItem value="current">Current Smoker</SelectItem>
                    </SelectContent>
                  </Select>
                  {(socialHistory.smoking_status === 'current' || socialHistory.smoking_status === 'former') && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Packs/Day</Label>
                        <Input 
                          type="number" 
                          step="0.5"
                          value={socialHistory.packs_per_day || ''} 
                          onChange={(e) => setSocialHistory(prev => ({ ...prev, packs_per_day: parseFloat(e.target.value) || null }))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Years Smoked</Label>
                        <Input 
                          type="number" 
                          value={socialHistory.years_smoked || ''} 
                          onChange={(e) => setSocialHistory(prev => ({ ...prev, years_smoked: parseInt(e.target.value) || null }))}
                        />
                      </div>
                      {packYears && (
                        <div className="col-span-2">
                          <Badge className="bg-orange-100 text-orange-800">
                            Pack-Years: {packYears}
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Alcohol */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Wine className="h-4 w-4 text-slate-500" />
                    <Label className="font-semibold">Alcohol</Label>
                  </div>
                  <Select 
                    value={socialHistory.alcohol_status} 
                    onValueChange={(v) => setSocialHistory(prev => ({ ...prev, alcohol_status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="occasional">Occasional</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="heavy">Heavy</SelectItem>
                    </SelectContent>
                  </Select>
                  {socialHistory.alcohol_status !== 'none' && (
                    <div>
                      <Label className="text-xs">Drinks/Week</Label>
                      <Input 
                        type="number" 
                        value={socialHistory.drinks_per_week || ''} 
                        onChange={(e) => setSocialHistory(prev => ({ ...prev, drinks_per_week: parseInt(e.target.value) || null }))}
                      />
                    </div>
                  )}
                </div>

                {/* Drug Use */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <Label className="font-semibold">Recreational Drug Use</Label>
                  <Select 
                    value={socialHistory.drug_use} 
                    onValueChange={(v) => setSocialHistory(prev => ({ ...prev, drug_use: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="past">Past Use</SelectItem>
                      <SelectItem value="current">Current Use</SelectItem>
                    </SelectContent>
                  </Select>
                  {socialHistory.drug_use !== 'none' && (
                    <Input 
                      placeholder="Details..."
                      value={socialHistory.drug_details || ''} 
                      onChange={(e) => setSocialHistory(prev => ({ ...prev, drug_details: e.target.value }))}
                    />
                  )}
                </div>

                {/* Occupation */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="h-4 w-4 text-slate-500" />
                    <Label className="font-semibold">Occupation</Label>
                  </div>
                  <Input 
                    value={socialHistory.occupation || ''} 
                    onChange={(e) => setSocialHistory(prev => ({ ...prev, occupation: e.target.value }))}
                    placeholder="Job title..."
                  />
                </div>

                {/* Marital Status */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <Label className="font-semibold">Marital Status</Label>
                  <Select 
                    value={socialHistory.marital_status || ''} 
                    onValueChange={(v) => setSocialHistory(prev => ({ ...prev, marital_status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                      <SelectItem value="partnered">Partnered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Living Situation */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <Label className="font-semibold">Living Situation</Label>
                  <Input 
                    value={socialHistory.living_situation || ''} 
                    onChange={(e) => setSocialHistory(prev => ({ ...prev, living_situation: e.target.value }))}
                    placeholder="e.g., Lives alone, with family..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={handleSaveSocialHistory} disabled={saving} data-testid="save-social-history-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Social History
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medications */}
        <TabsContent value="medications">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Pill className="h-5 w-5 text-green-500" />
                  Medication History
                </CardTitle>
                <CardDescription>Current and past medications</CardDescription>
              </div>
              <Button onClick={() => setMedicationDialogOpen(true)} size="sm" data-testid="add-medication-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Medication
              </Button>
            </CardHeader>
            <CardContent>
              {/* Current Medications */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Current Medications
                </h4>
                {history?.medications?.filter(m => m.is_current)?.length > 0 ? (
                  <div className="space-y-2">
                    {history.medications.filter(m => m.is_current).map((med, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {med.medication_name}
                            {med.is_otc && <Badge variant="outline" className="text-xs">OTC</Badge>}
                            {med.is_supplement && <Badge variant="outline" className="text-xs">Supplement</Badge>}
                          </p>
                          <p className="text-sm text-slate-600">
                            {med.dose && `${med.dose} `}
                            {med.frequency && `• ${med.frequency} `}
                            {med.route && `• ${med.route}`}
                          </p>
                          {med.indication && (
                            <p className="text-xs text-slate-500">For: {med.indication}</p>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            const allMeds = history.medications;
                            const currentIdx = allMeds.findIndex(m => m.medication_name === med.medication_name && m.is_current);
                            handleDeleteItem('medications', currentIdx);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No current medications</p>
                )}
              </div>

              {/* Past Medications */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  Past Medications
                </h4>
                {history?.medications?.filter(m => !m.is_current)?.length > 0 ? (
                  <div className="space-y-2">
                    {history.medications.filter(m => !m.is_current).map((med, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-600">{med.medication_name}</p>
                          <p className="text-sm text-slate-500">
                            {med.dose && `${med.dose} `}
                            {med.frequency && `• ${med.frequency}`}
                            {med.end_date && ` • Stopped: ${med.end_date}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No past medications recorded</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Condition Dialog */}
      <Dialog open={conditionDialogOpen} onOpenChange={setConditionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Medical Condition</DialogTitle>
            <DialogDescription>Add a condition to past medical history</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Condition *</Label>
              <Select value={newCondition.condition} onValueChange={(v) => setNewCondition(prev => ({ ...prev, condition: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select or type condition..." />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CONDITIONS.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input 
                className="mt-2"
                placeholder="Or type custom condition..."
                value={COMMON_CONDITIONS.includes(newCondition.condition) ? '' : newCondition.condition}
                onChange={(e) => setNewCondition(prev => ({ ...prev, condition: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Year Diagnosed</Label>
                <Input 
                  type="number" 
                  placeholder="e.g., 2020"
                  value={newCondition.year_diagnosed}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, year_diagnosed: e.target.value }))}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={newCondition.status} onValueChange={(v) => setNewCondition(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="managed">Managed</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={newCondition.notes}
                onChange={(e) => setNewCondition(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConditionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCondition} disabled={saving} data-testid="submit-condition-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Condition'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Allergy Dialog */}
      <Dialog open={allergyDialogOpen} onOpenChange={setAllergyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Allergy</DialogTitle>
            <DialogDescription>Record a drug, food, or environmental allergy</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Allergen *</Label>
              <Input 
                placeholder="e.g., Penicillin, Peanuts..."
                value={newAllergy.allergen}
                onChange={(e) => setNewAllergy(prev => ({ ...prev, allergen: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={newAllergy.allergy_type} onValueChange={(v) => setNewAllergy(prev => ({ ...prev, allergy_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drug">Drug</SelectItem>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="environmental">Environmental</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={newAllergy.severity} onValueChange={(v) => setNewAllergy(prev => ({ ...prev, severity: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mild">Mild</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="severe">Severe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reaction</Label>
              <Textarea 
                placeholder="Describe the reaction..."
                value={newAllergy.reaction}
                onChange={(e) => setNewAllergy(prev => ({ ...prev, reaction: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllergyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddAllergy} disabled={saving} data-testid="submit-allergy-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Allergy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Family History Dialog */}
      <Dialog open={familyDialogOpen} onOpenChange={setFamilyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Family History</DialogTitle>
            <DialogDescription>Record medical conditions in family members</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Relative *</Label>
                <Select value={newFamily.relative} onValueChange={(v) => setNewFamily(prev => ({ ...prev, relative: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select relative..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIVES.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Condition *</Label>
                <Select value={newFamily.condition} onValueChange={(v) => setNewFamily(prev => ({ ...prev, condition: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_CONDITIONS.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Age at Diagnosis</Label>
                <Input 
                  placeholder="e.g., 50"
                  value={newFamily.age_diagnosed}
                  onChange={(e) => setNewFamily(prev => ({ ...prev, age_diagnosed: e.target.value }))}
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox 
                  id="is_alive" 
                  checked={newFamily.is_alive}
                  onCheckedChange={(checked) => setNewFamily(prev => ({ ...prev, is_alive: checked }))}
                />
                <label htmlFor="is_alive">Currently Living</label>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={newFamily.notes}
                onChange={(e) => setNewFamily(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFamilyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFamilyHistory} disabled={saving} data-testid="submit-family-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Surgery Dialog */}
      <Dialog open={surgeryDialogOpen} onOpenChange={setSurgeryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Surgical History</DialogTitle>
            <DialogDescription>Record past surgeries and procedures</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Procedure *</Label>
              <Input 
                placeholder="e.g., Appendectomy, C-section..."
                value={newSurgery.procedure}
                onChange={(e) => setNewSurgery(prev => ({ ...prev, procedure: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Body Site</Label>
                <Input 
                  placeholder="e.g., Right knee, Abdomen..."
                  value={newSurgery.body_site}
                  onChange={(e) => setNewSurgery(prev => ({ ...prev, body_site: e.target.value }))}
                />
              </div>
              <div>
                <Label>Year</Label>
                <Input 
                  type="number"
                  placeholder="e.g., 2020"
                  value={newSurgery.year}
                  onChange={(e) => setNewSurgery(prev => ({ ...prev, year: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Hospital/Facility</Label>
              <Input 
                placeholder="Where was the surgery performed?"
                value={newSurgery.hospital}
                onChange={(e) => setNewSurgery(prev => ({ ...prev, hospital: e.target.value }))}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="had_complications" 
                checked={newSurgery.had_complications}
                onCheckedChange={(checked) => setNewSurgery(prev => ({ ...prev, had_complications: checked }))}
              />
              <label htmlFor="had_complications">Had Complications</label>
            </div>
            {newSurgery.had_complications && (
              <div>
                <Label>Complications</Label>
                <Textarea 
                  placeholder="Describe complications..."
                  value={newSurgery.complications}
                  onChange={(e) => setNewSurgery(prev => ({ ...prev, complications: e.target.value }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSurgeryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSurgery} disabled={saving} data-testid="submit-surgery-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Surgery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Medication Dialog */}
      <Dialog open={medicationDialogOpen} onOpenChange={setMedicationDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Medication</DialogTitle>
            <DialogDescription>Record current or past medications</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Medication Name *</Label>
              <Input 
                placeholder="e.g., Metformin, Lisinopril..."
                value={newMedication.medication_name}
                onChange={(e) => setNewMedication(prev => ({ ...prev, medication_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Dose</Label>
                <Input 
                  placeholder="e.g., 500mg"
                  value={newMedication.dose}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, dose: e.target.value }))}
                />
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={newMedication.frequency} onValueChange={(v) => setNewMedication(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Route</Label>
                <Select value={newMedication.route} onValueChange={(v) => setNewMedication(prev => ({ ...prev, route: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDICATION_ROUTES.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Indication</Label>
              <Input 
                placeholder="What is it for?"
                value={newMedication.indication}
                onChange={(e) => setNewMedication(prev => ({ ...prev, indication: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={newMedication.start_date}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="flex items-center space-x-4 pt-6">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="is_current" 
                    checked={newMedication.is_current}
                    onCheckedChange={(checked) => setNewMedication(prev => ({ ...prev, is_current: checked }))}
                  />
                  <label htmlFor="is_current">Current</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="is_otc" 
                    checked={newMedication.is_otc}
                    onCheckedChange={(checked) => setNewMedication(prev => ({ ...prev, is_otc: checked }))}
                  />
                  <label htmlFor="is_otc">OTC</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="is_supplement" 
                    checked={newMedication.is_supplement}
                    onCheckedChange={(checked) => setNewMedication(prev => ({ ...prev, is_supplement: checked }))}
                  />
                  <label htmlFor="is_supplement">Supplement</label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMedicationDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMedication} disabled={saving} data-testid="submit-medication-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Medication'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
